import { CustomCarrierCodes } from "@jti/commerce/shared";

import type { ShippingFormFieldsDefaultValues } from "@checkout-v2/forms";
import { isDeliveryOptionSelectable } from "@checkout-v2/shared/utils/prepareDeliveryOptions";

import type { UseCheckoutFastFlowArgs } from "../../useCheckoutFastFlow";
import { getOrderAttributesDefaultValues } from "../getOrderAttributesDefaultValues";
import { hasRequiredOrderAttribute } from "../hasRequiredOrderAttribute";
import { pickDeliveryOption } from "../pickDeliveryOption";

import type { FastFlowShippingStepCandidateReady } from ".";

export const isFastFlowShippingStepCandidateReady = (
  candidate: ShippingFormFieldsDefaultValues | null
): candidate is FastFlowShippingStepCandidateReady => {
  return candidate !== null;
};

export const buildShippingStepFastFlowCandidate = ({
  initialDefaultValues,
  hasDefaultAddress,
  allDeliveryOptions: availableDeliveryOptions,
  isSubscription,
  checkoutConfiguration,
  lastOrderData,
  definedOrderAttributes,
  isShippingAgeVerificationEnabled,
  isAgeVerified,
}: Pick<
  UseCheckoutFastFlowArgs,
  | "initialDefaultValues"
  | "hasDefaultAddress"
  | "allDeliveryOptions"
  | "isSubscription"
  | "checkoutConfiguration"
  | "lastOrderData"
  | "definedOrderAttributes"
  | "isShippingAgeVerificationEnabled"
  | "isAgeVerified"
>): ShippingFormFieldsDefaultValues | null => {
  if (!initialDefaultValues) {
    return null;
  }

  /**
   * checks if "atHome" shipping method is available in the configuration
   * if not - fallback to empty form state immediately
   */
  const atHomeMethod = checkoutConfiguration.shippingMethods.find((method) => method.type === "atHome");

  if (!atHomeMethod) {
    return null;
  }

  if (isShippingAgeVerificationEnabled && !isAgeVerified && atHomeMethod.disableForNotAgeVerified) {
    return null;
  }

  /**
   * Ensures default shipping address from user profile is present.
   * If no default address exists, fall back to empty form state immediately.
   */
  if (!hasDefaultAddress) {
    return null;
  }

  /**
   * Required order attributes must be explicitly filled by the user.
   * We cannot pre-fill these during fast-forward.
   * If any required attribute exists, skip fast-forward immediately.
   */
  if (hasRequiredOrderAttribute(definedOrderAttributes)) {
    return null;
  }

  const candidate: ShippingFormFieldsDefaultValues = { ...initialDefaultValues };
  const eligibleDeliveryOptions = availableDeliveryOptions.filter(
    (option) =>
      option.carrierCode !== CustomCarrierCodes.jtiPickup && isDeliveryOptionSelectable(option, isSubscription)
  );

  candidate.shippingMethodCode = atHomeMethod.methodCode;

  /**
   * Prefill Delivery Options logic:
   * 1. If no eligible delivery options available → skip fast-forward
   * 2. If exactly 1 option → use it
   * 3. If multiple options → check against last order delivery option
   *    - If match found → use it
   *    - If no match → skip fast-forward
   */
  if (eligibleDeliveryOptions.length === 0) {
    return null;
  }

  if (eligibleDeliveryOptions.length === 1) {
    candidate.deliveryOption = eligibleDeliveryOptions[0].methodCode;
  }

  if (eligibleDeliveryOptions.length > 1) {
    const lastOrderDeliveryOption = pickDeliveryOption(eligibleDeliveryOptions, lastOrderData?.shipping_method_code);

    if (!lastOrderDeliveryOption) {
      return null;
    }

    candidate.deliveryOption = lastOrderDeliveryOption;
  }

  /**
   * Prefill Order Attributes from last order.
   * Match last order's additional_attributes against defined order attributes
   * and populate only matching attributes.
   */
  const orderAttributesDefaults = getOrderAttributesDefaultValues(
    definedOrderAttributes,
    lastOrderData?.additional_attributes
  );

  if (Object.keys(orderAttributesDefaults).length > 0) {
    (
      candidate as ShippingFormFieldsDefaultValues & { orderAttributes?: Record<string, string | boolean> }
    ).orderAttributes = orderAttributesDefaults;
  }

  return candidate;
};
